import { Fragment, useState, type MouseEvent, type ReactNode } from 'react';
import { useShellTexts } from '../../i18n/use-shell-texts';
import { Button } from '../button';
import type { ButtonVariant } from '../button-class';
import { Chip } from '../chip';
import { EmptyState, ErrorState, LoadingState } from '../feedback';
import { Icon } from '../icon';
import { resolveCellStyle } from './cell-styles';
import { enumBadgeColor, formatCellDate, formatCellEnum, formatCellNumber } from './format';
import { TablePaginator } from './paginator';
import { nextSort } from './sort';
import type {
  TableAction,
  TableColumn,
  TablePagination,
  TableSort,
  TableStyleVariant,
} from './types';
import { getValue } from './value';

/**
 * The feature screens' table, ported from portal-ui-lib's `data-table-v2` `TableComponent`.
 * Controlled: rows, sort, pagination and selection belong to the caller. Loading and errors are
 * overlays, so the table keeps its size; the empty state shows only without rows and without an
 * error. See docs/dealer-portal-react/ui.md.
 */

export interface DataTableProps<T> {
  rows: readonly T[];
  columns: readonly TableColumn<T>[];
  /** The row's identity: a dot path (default `id`) or a function. */
  rowId?: string | ((row: T) => string | number);
  /** Accessible name of the table. */
  caption?: string;
  loading?: boolean;
  loadingMessage?: string;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  emptySubMessage?: string;
  emptyAction?: ReactNode;
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort) => void;
  pagination?: TablePagination | null;
  onPageChange?: (page: { pageIndex: number; pageSize: number }) => void;
  /** See `TablePaginator`. */
  pagerAlign?: 'between' | 'center';
  formatPageRange?: (range: { start: number; end: number; total: number }) => string;
  selectable?: boolean;
  selectedRows?: readonly T[];
  onSelectionChange?: (rows: T[]) => void;
  onRowClick?: (row: T, event: MouseEvent<HTMLTableRowElement>) => void;
  onAction?: (actionId: string, row: T, event: MouseEvent<HTMLButtonElement>) => void;
  /** A full-width row under the row, when this returns content (portal-ui-lib's expanded row). */
  renderExpanded?: (row: T) => ReactNode | null;
  /** Zebra rows (`table-striped`). */
  striped?: boolean;
  /** Highlight the row under the pointer (`table-hover`); on by itself with `onRowClick`. */
  hover?: boolean;
  variant?: TableStyleVariant;
  /** Inline cells: a text/number edit was committed. */
  onInlineCommit?: (row: T, column: TableColumn<T>, value: string | number | null) => void;
  /** Inline cells: a select changed. */
  onInlineSelect?: (row: T, column: TableColumn<T>, value: string) => void;
  /** Inline cells: a switch was toggled. */
  onInlineToggle?: (row: T, column: TableColumn<T>, value: boolean) => void;
  /** Inline editors disabled, e.g. while a save is in flight. */
  inlineDisabled?: boolean;
}

const STYLES: Record<
  TableStyleVariant,
  { container: string; table: string; header: string; th: string; td: string; row: string }
> = {
  // The legacy Bootstrap `.table`: no frame of its own (the surrounding `Card` draws it), 12px
  // cells divided by top borders, a 2px rule under a bold sentence-case header.
  default: {
    container: 'relative overflow-x-auto',
    table: 'w-full border-collapse text-left text-sm text-ink',
    header: '',
    th: 'border-t border-b-2 border-line p-3 align-bottom text-sm font-bold text-ink',
    td: 'border-t border-line p-3 align-top',
    row: '',
  },
  // Grouped catalogues: fixed layout so declared widths hold; a word longer than its column
  // breaks rather than running into the next one.
  catalog: {
    container: 'relative overflow-x-auto',
    table: 'w-full table-fixed border-collapse text-left text-sm text-ink',
    header: '',
    th: 'border-t border-b-2 border-line p-3 align-bottom text-sm font-bold text-ink [overflow-wrap:anywhere]',
    td: 'border-t border-line p-3 align-top [overflow-wrap:anywhere]',
    row: '',
  },
};

const ACTION_VARIANTS: Record<NonNullable<TableAction['color']>, ButtonVariant> = {
  primary: 'primary',
  secondary: 'secondary',
  danger: 'danger',
  warning: 'warning',
  success: 'success',
  info: 'info',
};

function rowIdOf<T>(row: T, rowId: DataTableProps<T>['rowId']): string | number {
  if (typeof rowId === 'function') {
    return rowId(row);
  }
  const value = getValue(row, rowId ?? 'id');
  return typeof value === 'number' ? value : String(value ?? '');
}

function alignClass(align: 'left' | 'center' | 'right' | undefined): string {
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '';
}

function size(value: string | number | undefined): string | undefined {
  return typeof value === 'number' ? `${value}px` : value;
}

/** A small pair of triangles, the active one in ink (legacy showed next to no sort marker). */
function SortIcon({ direction }: { direction: 'asc' | 'desc' | null }) {
  return (
    <span className="inline-flex flex-col gap-px" aria-hidden="true">
      <svg
        className={`h-[3px] w-[6px] ${direction === 'asc' ? 'text-ink' : 'text-line'}`}
        viewBox="0 0 6 3"
        fill="currentColor"
      >
        <path d="M3 0l3 3H0z" />
      </svg>
      <svg
        className={`h-[3px] w-[6px] ${direction === 'desc' ? 'text-ink' : 'text-line'}`}
        viewBox="0 0 6 3"
        fill="currentColor"
      >
        <path d="M3 3L0 0h6z" />
      </svg>
    </span>
  );
}

function HeaderCell<T>({
  column,
  sort,
  thClass,
  onSortChange,
}: {
  column: TableColumn<T>;
  sort: TableSort | null | undefined;
  thClass: string;
  onSortChange?: (sort: TableSort) => void;
}) {
  const style = resolveCellStyle(column.cellStyle);
  const align = style?.align ?? column.align;
  const field = typeof column.field === 'string' ? column.field : column.key;
  const direction = sort?.field === field ? sort.direction : null;
  const classes = `${thClass} ${alignClass(align)} ${style?.className ?? ''} ${column.headerClass ?? ''}`;
  const widthStyle = { width: size(column.width), minWidth: size(column.minWidth) };

  if (column.headerSortOptions?.length && onSortChange) {
    return (
      <th scope="col" className={classes} style={widthStyle}>
        {column.label ? <span className="mb-1 block">{column.label}</span> : null}
        <span className="flex gap-2">
          {column.headerSortOptions.map(option => {
            const optionDirection = sort?.field === option.field ? sort.direction : null;
            return (
              <button
                key={option.field}
                type="button"
                onClick={() => onSortChange(nextSort(sort, option.field))}
                className={`inline-flex items-center gap-1 hover:text-brand ${optionDirection ? 'underline' : ''}`}
                aria-pressed={optionDirection !== null}
              >
                {option.label}
                <SortIcon direction={optionDirection} />
              </button>
            );
          })}
        </span>
      </th>
    );
  }

  if (column.sortable && onSortChange) {
    return (
      <th
        scope="col"
        className={classes}
        style={widthStyle}
        aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
      >
        <button
          type="button"
          onClick={() => onSortChange(nextSort(sort, field))}
          className={`inline-flex items-center gap-1 font-bold hover:text-brand ${direction ? 'underline' : ''} ${align === 'right' ? 'flex-row-reverse' : ''}`}
        >
          {column.label}
          <SortIcon direction={direction} />
        </button>
      </th>
    );
  }

  return (
    <th scope="col" className={classes} style={widthStyle}>
      {column.header ?? column.label}
    </th>
  );
}

function InlineCell<T>({
  row,
  column,
  disabled,
  onCommit,
  onSelect,
  onToggle,
}: {
  row: T;
  column: TableColumn<T>;
  disabled: boolean;
  onCommit?: DataTableProps<T>['onInlineCommit'];
  onSelect?: DataTableProps<T>['onInlineSelect'];
  onToggle?: DataTableProps<T>['onInlineToggle'];
}) {
  const config = column.inline!;
  const value = getValue(row, column.field);
  const editable = config.editable ? config.editable(row) : true;
  const [draft, setDraft] = useState<string | null>(null);
  const display = config.display
    ? config.display(row)
    : value === null || value === undefined
      ? ''
      : String(value);

  if (!editable) {
    return <>{display}</>;
  }
  if (config.kind === 'checkbox') {
    const on = value === true;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={column.label}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation();
          onToggle?.(row, column, !on);
        }}
        className="inline-flex items-center gap-2 disabled:opacity-50"
      >
        <span
          className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${on ? 'bg-brand' : 'bg-line'}`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`}
          />
        </span>
        {config.booleanLabels ? (
          <span>{on ? config.booleanLabels.true : config.booleanLabels.false}</span>
        ) : null}
      </button>
    );
  }
  if (config.kind === 'select') {
    return (
      <select
        aria-label={column.label}
        value={value === null || value === undefined ? '' : String(value)}
        disabled={disabled}
        onClick={event => event.stopPropagation()}
        onChange={event => onSelect?.(row, column, event.target.value)}
        className="rounded-bs border border-input-border px-2 py-1 text-sm text-ink-soft focus:border-input-focus focus:outline-none disabled:opacity-50"
      >
        {(config.options ?? []).map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  if (draft === null) {
    return (
      <button
        type="button"
        disabled={disabled}
        aria-label={
          value === null || value === undefined || value === ''
            ? column.label
            : `${column.label}: ${String(value)}`
        }
        onClick={event => {
          event.stopPropagation();
          setDraft(value === null || value === undefined ? '' : String(value));
        }}
        className="w-full rounded-bs px-1 text-left hover:bg-black/5 disabled:opacity-50"
      >
        {display || <span className="text-ink-muted">—</span>}
      </button>
    );
  }
  const commit = () => {
    const committed =
      config.kind === 'number' ? (draft.trim() === '' ? null : Number(draft)) : draft;
    setDraft(null);
    if (committed !== (value ?? (config.kind === 'number' ? null : ''))) {
      onCommit?.(row, column, committed);
    }
  };
  return (
    <input
      autoFocus
      aria-label={column.label}
      type={config.kind === 'number' ? 'number' : 'text'}
      value={draft}
      disabled={disabled}
      onClick={event => event.stopPropagation()}
      onChange={event => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          commit();
        } else if (event.key === 'Escape') {
          setDraft(null);
        }
      }}
      className="w-full rounded-bs border border-input-focus px-2 py-1 text-sm text-ink-soft focus:outline-none"
    />
  );
}

function CellContent<T>({
  row,
  column,
  props,
}: {
  row: T;
  column: TableColumn<T>;
  props: DataTableProps<T>;
}) {
  const t = useShellTexts().kit;
  const value = getValue(row, column.field);
  const style = resolveCellStyle(column.cellStyle);
  switch (column.type) {
    case 'number':
      return <>{formatCellNumber(value, style?.format ?? column.format)}</>;
    case 'date':
      return <>{formatCellDate(value, column.format)}</>;
    case 'boolean':
      return value === null || value === undefined ? null : (
        <Chip variant={value ? 'success' : 'danger'}>
          <Icon name={value ? 'check' : 'close'} />
          {value ? t.yes : t.no}
        </Chip>
      );
    case 'enum':
      return value === null || value === undefined ? null : (
        <Chip className={enumBadgeColor(value, column.enumOptions)}>
          {formatCellEnum(value, column.enumOptions)}
        </Chip>
      );
    case 'custom':
      return (
        <>
          {column.render
            ? column.render(row)
            : value === null || value === undefined
              ? ''
              : String(value)}
        </>
      );
    case 'inline':
      return (
        <InlineCell
          row={row}
          column={column}
          disabled={props.inlineDisabled ?? false}
          onCommit={props.onInlineCommit}
          onSelect={props.onInlineSelect}
          onToggle={props.onInlineToggle}
        />
      );
    case 'action': {
      const actions = (column.actions ?? []).filter(action =>
        action.visible ? action.visible(row) : true,
      );
      return (
        <span className="inline-flex flex-wrap justify-end gap-1">
          {actions.map(action => (
            <Button
              key={action.id}
              size="sm"
              variant={ACTION_VARIANTS[action.color ?? 'secondary']}
              icon={action.icon}
              iconOnly={action.iconOnly === true && action.icon !== undefined}
              title={action.title ?? action.tooltip}
              disabled={action.disabled ? action.disabled(row) : false}
              onClick={event => {
                event.stopPropagation();
                props.onAction?.(action.id, row, event);
              }}
            >
              {action.label}
            </Button>
          ))}
        </span>
      );
    }
    default:
      return <>{value === null || value === undefined ? '' : String(value)}</>;
  }
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    rows,
    columns,
    rowId,
    caption,
    loading = false,
    error = null,
    sort,
    pagination,
    selectable = false,
    selectedRows = [],
    striped = false,
    variant = 'default',
  } = props;
  const hover = props.hover ?? props.onRowClick !== undefined;
  const t = useShellTexts().kit;
  const styles = STYLES[variant];
  const visible = columns.filter(column => column.visible !== false);
  const selectedIds = new Set(selectedRows.map(row => rowIdOf(row, rowId)));
  const allSelected = rows.length > 0 && rows.every(row => selectedIds.has(rowIdOf(row, rowId)));
  const someSelected = !allSelected && rows.some(row => selectedIds.has(rowIdOf(row, rowId)));
  const colSpan = visible.length + (selectable ? 1 : 0);

  const toggleRow = (row: T, checked: boolean) => {
    const id = rowIdOf(row, rowId);
    props.onSelectionChange?.(
      checked
        ? [...selectedRows, row]
        : selectedRows.filter(candidate => rowIdOf(candidate, rowId) !== id),
    );
  };

  return (
    <div className={styles.container}>
      <div className="relative">
        <table className={styles.table} aria-busy={loading || undefined}>
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead className={styles.header}>
            <tr>
              {selectable ? (
                <th scope="col" className={`w-12 ${styles.th}`}>
                  <input
                    type="checkbox"
                    aria-label={t.selectAll}
                    checked={allSelected}
                    ref={element => {
                      if (element) {
                        element.indeterminate = someSelected;
                      }
                    }}
                    onChange={event =>
                      props.onSelectionChange?.(event.target.checked ? [...rows] : [])
                    }
                    className="h-4 w-4 accent-brand"
                  />
                </th>
              ) : null}
              {visible.map(column => (
                <HeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  thClass={styles.th}
                  onSortChange={props.onSortChange}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="p-0 align-top">
                  {error ? (
                    <div className="min-h-48" />
                  ) : (
                    <EmptyState
                      message={props.emptyMessage}
                      subMessage={props.emptySubMessage}
                      action={props.emptyAction}
                    />
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const id = rowIdOf(row, rowId);
                const expanded = props.renderExpanded?.(row) ?? null;
                return (
                  <Fragment key={id}>
                    <tr
                      className={`${styles.row} ${striped && index % 2 === 0 ? 'bg-black/5' : ''} ${hover ? 'hover:bg-black/[.075]' : ''} ${props.onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={event => {
                        const target = event.target as HTMLElement;
                        if (
                          !props.onRowClick ||
                          target.closest('button, a, input, select, textarea, label')
                        ) {
                          return;
                        }
                        props.onRowClick(row, event);
                      }}
                    >
                      {selectable ? (
                        <td className={styles.td}>
                          <input
                            type="checkbox"
                            aria-label={t.selectRow}
                            checked={selectedIds.has(id)}
                            onChange={event => toggleRow(row, event.target.checked)}
                            className="h-4 w-4 accent-brand"
                          />
                        </td>
                      ) : null}
                      {visible.map(column => {
                        const style = resolveCellStyle(column.cellStyle);
                        const extra =
                          typeof column.cellClass === 'function'
                            ? column.cellClass(row)
                            : (column.cellClass ?? '');
                        return (
                          <td
                            key={column.key}
                            className={`${styles.td} ${alignClass(style?.align ?? column.align)} ${style?.className ?? ''} ${extra}`}
                          >
                            <CellContent row={row} column={column} props={props} />
                          </td>
                        );
                      })}
                    </tr>
                    {expanded ? (
                      <tr className={styles.row}>
                        <td colSpan={colSpan} className="p-0">
                          {expanded}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        {loading || error ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
            {error ? (
              <div className="mx-4 w-full max-w-lg rounded-bs border border-line bg-white">
                <ErrorState message={error} onRetry={props.onRetry} />
              </div>
            ) : (
              <LoadingState message={props.loadingMessage} />
            )}
          </div>
        ) : null}
      </div>
      {pagination && props.onPageChange ? (
        <TablePaginator
          pagination={pagination}
          pageItemCount={rows.length}
          disabled={loading || !!error}
          align={props.pagerAlign}
          formatRange={props.formatPageRange}
          onPageChange={props.onPageChange}
        />
      ) : null}
    </div>
  );
}
